import { buildCatalogue, summariseCategories } from './storage';
import type {
  InsightTag,
  InsightTagCategory,
  InsightTagsCatalogue
} from './types';

/** Ross `RelationshipTagCategoryResource` shape. */
export type ApiRelationshipTagCategory = {
  id: string;
  name: string;
  color_hex: string;
  created_at?: string | null;
  updated_at?: string | null;
};

/** Ross `RelationshipTagResource` shape. */
export type ApiRelationshipTag = {
  id: string;
  tag_key: string;
  tag_name: string;
  category: string | null;
  relationship_tag_category_id: string | null;
  category_details: ApiRelationshipTagCategory | null;
  description: string;
  is_active: boolean;
  display_order: number;
  created_at?: string | null;
  updated_at?: string | null;
};

/**
 * Derive snake_case `tag_key` from a display name.
 * Matches BE regex: /^[a-z0-9]+(?:_[a-z0-9]+)*$/
 */
export function deriveTagKey(name: string): string {
  const key = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
  return key.length > 0 ? key : 'tag';
}

export function deriveTagKeyWithSuffix(name: string): string {
  const base = deriveTagKey(name);
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}_${suffix}`;
}

export function mapApiTagToInsightTag(tag: ApiRelationshipTag): InsightTag {
  return {
    id: tag.id,
    name: tag.tag_name,
    description: tag.description ?? '',
    is_active: Boolean(tag.is_active),
    // Usage analytics not on Ross API yet — stub for Settings table.
    times_applied: 0,
    last_applied_at: null
  };
}

export function mapApiCategoryToInsightCategory(
  category: ApiRelationshipTagCategory,
  tags: InsightTag[] = []
): InsightTagCategory {
  return {
    id: category.id,
    name: category.name,
    colour: category.color_hex,
    tags
  };
}

/**
 * Compose Settings catalogue from flat category + tag lists.
 * Tags without a matching category id are skipped (orphans).
 */
export function composeCatalogueFromApi(
  categories: ApiRelationshipTagCategory[],
  tags: ApiRelationshipTag[]
): InsightTagsCatalogue {
  const tagsByCategoryId = new Map<string, InsightTag[]>();

  for (const tag of tags) {
    const categoryId = tag.relationship_tag_category_id;
    if (!categoryId) continue;
    const existing = tagsByCategoryId.get(categoryId) ?? [];
    existing.push(mapApiTagToInsightTag(tag));
    tagsByCategoryId.set(categoryId, existing);
  }

  const mapped: InsightTagCategory[] = categories.map((category) =>
    mapApiCategoryToInsightCategory(
      category,
      tagsByCategoryId.get(category.id) ?? []
    )
  );

  return buildCatalogue(mapped);
}

export function isTagKeyValidationConflict(error: unknown): boolean {
  const err = error as {
    response?: {
      status?: number;
      data?: { details?: { tag_key?: unknown } };
    };
  };
  return (
    err?.response?.status === 422 &&
    err?.response?.data?.details?.tag_key !== undefined
  );
}

export { summariseCategories };
